<script lang="ts" setup>
const props = defineProps<{
  previewUrl: string;
  title: string;
  alwaysWhite?: boolean;
  albumCoverUrl?: string;
  withAlbumCover?: boolean;
  showId?: string;
}>();

const SongRowPreviewPlayback = defineAsyncComponent(
  () => import("@/components/SearchResults/SongRowPreviewPlayback.vue"),
);
const playback = ref<InstanceType<typeof SongRowPreviewPlayback>>();
const active = ref(false);

watch(active, (value) => {
  if (value) {
    props.showId && emitter.emit("alwaysShowId", props.showId);
  }
});

onMounted(() => {
  emitter.on("deactivateAllPreviews", deactivate);
});

onBeforeUnmount(() => {
  emitter.off("deactivateAllPreviews", deactivate);
});

function deactivate() {
  active.value = false;
}

async function toggle() {
  if (!active.value) {
    emitter.emit("deactivateAllPreviews");
    await nextTick();
    active.value = true;
  } else {
    playback.value?.toggle();
  }
}
</script>
<template>
  <button
    v-if="previewUrl"
    class="songpreview sound-sample-preview-toggle-button"
    :class="{
      'always-white': alwaysWhite,
      'always-show': active,
      'with-album-cover': withAlbumCover,
    }"
    type="button"
    :aria-label="
      playback?.ariaLabel || $t('SV.LIT_PausedPreviewOf', { title: title })
    "
    :data-status="playback?.status || 'paused'"
    data-user-search-activity="Played Sound Sample"
    @click.stop.prevent="toggle()"
  >
    <div
      v-if="withAlbumCover"
      class="album-artwork-wrapper"
    >
      <Image
        v-if="albumCoverUrl"
        class=""
        :src="albumCoverUrl"
        :alt="title"
        rounded
        :restrict-width="80"
      />
      <div
        v-else
        class="empty-album-cover"
      >
        <FAIcon class="fa-light fa-music" />
      </div>
    </div>
    <SongRowPreviewPlayback
      v-if="active"
      ref="playback"
      :preview-url="previewUrl"
      :title="title"
    />
    <div
      v-else
      class="icon-overlay"
    >
      <div class="songpreview-icon">
        <FAIcon class="fa-fw fa-solid fa-play" />
      </div>
    </div>
  </button>
</template>

<style scoped lang="postcss">
.songpreview-icon {
  font-size: 1.25em;
  @apply text-gray-400 hover:text-gray-300;
}

button:focus-visible {
  outline: 3px solid theme(colors.gray.900);
}
button.always-show {
  opacity: 100% !important;
}
button.always-white {
  &:focus-visible {
    outline-color: white !important;
  }
  :deep() {
    .songpreview-icon {
      i,
      svg {
        color: white !important;
      }
    }

    .progress-ring__circle {
      stroke: white !important;
    }
  }
}

button {
  @apply relative inline-block h-full w-full p-0;
}

button.with-album-cover {
  @apply h-12 w-12;
}
.album-artwork-wrapper {
  @apply h-12 w-12;
  :deep() {
    img {
      @apply shadow-lg;
    }
  }
}

.icon-overlay {
  @apply absolute bottom-0 left-0 right-0 top-0 flex items-center justify-center transition-all;
}

button.with-album-cover {
  :deep() {
    .icon-overlay {
      @apply opacity-0 transition-all;
    }
  }
  &:hover,
  &:focus-visible,
  &.always-show {
    :deep() {
      .icon-overlay {
        @apply opacity-100;
      }
    }
    .album-artwork-wrapper {
      @apply brightness-75;
    }
  }
}
.empty-album-cover {
  @apply flex h-full w-full items-center justify-center rounded bg-base-300 text-secondary;
}
.bg-circle {
  @apply bg-base-100;
}

.with-album-cover .songpreview-icon {
  @apply text-white;
}
</style>
